'use client'

import { memo, useMemo, useState } from 'react'
import { IconBuilding, IconHome2 } from '@tabler/icons-react'

import { FormMode } from 'shared/types/forms'
import Button from 'shared/ui/Button'
import Tabs, { TabItem } from 'shared/ui/Tabs/Tabs'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import ProfileAddressSingleItem from '../components/ProfileAddressSingleItem'
import ModalAddInvoiceAddress from '../submodules/addresses/components/ModalAddInvoiceAddress'
import {
  CustomerInvoiceAddress,
  InvoiceType
} from '../submodules/addresses/types'
import { IS_PERSONAL_INVOICE_ENABLED } from '../submodules/addresses/utils'

import styles from './ProfileInvoiceDetails.module.scss'

const tabs: TabItem<InvoiceType>[] = [
  {
    id: 'faktury-firmowe',
    label: 'Faktury na firmę',
    icon: <IconBuilding />,
    value: InvoiceType.company,
    panelId: 'panel-2'
  },
  {
    id: 'faktury-imienne',
    label: 'Faktury imienne',
    icon: <IconHome2 />,
    value: InvoiceType.personal,
    panelId: 'panel-1'
  }
]

interface ProfileInvoiceDetailsProps {
  addresses: CustomerInvoiceAddress[]
}

interface InvoiceTypeTabsProps {
  selected: InvoiceType
  onTabChange: (tab: InvoiceType) => void
}

const InvoiceTypeTabs = memo(
  ({ selected, onTabChange }: InvoiceTypeTabsProps) => (
    <Tabs<InvoiceType>
      tabs={tabs}
      selected={selected}
      onTabChange={onTabChange}
      ariaLabel='Typy danych do faktury'
    />
  )
)

const ProfileInvoiceDetails = ({ addresses }: ProfileInvoiceDetailsProps) => {
  const [activeTab, setActiveTab] = useState<InvoiceType>(InvoiceType.company)

  const companyAddresses = useMemo(
    () =>
      IS_PERSONAL_INVOICE_ENABLED
        ? addresses.filter(
            address => address.invoiceType === InvoiceType.company
          )
        : addresses,
    [addresses]
  )
  const personalAddresses = useMemo(
    () =>
      addresses.filter(address => address.invoiceType === InvoiceType.personal),
    [addresses]
  )

  const personalPanel = useMemo(
    () => (
      <div id='panel-1' role='tabpanel' aria-labelledby='faktury-imienne'>
        {isNotNilOrEmpty(personalAddresses) ? (
          personalAddresses.map((address, index) => (
            <ProfileAddressSingleItem
              id={`profile_invoice_address_${index}`}
              type='invoice'
              key={address.id}
              address={address}
            />
          ))
        ) : (
          <div>Nie dodano jeszcze żadnych danych do faktury imiennej.</div>
        )}
      </div>
    ),
    [personalAddresses]
  )

  const companyPanel = useMemo(
    () => (
      <div id='panel-2' role='tabpanel' aria-labelledby='faktury-firmowe'>
        {isNotNilOrEmpty(companyAddresses) ? (
          companyAddresses.map((address, index) => (
            <ProfileAddressSingleItem
              id={`profile_invoice_address_${index}`}
              type='invoice'
              key={address.id}
              address={address}
            />
          ))
        ) : (
          <p>Nie dodano jeszcze żadnych danych do faktury firmowej.</p>
        )}
      </div>
    ),
    [companyAddresses]
  )

  return (
    <div>
      <div className={styles.header}>
        <div
          className={styles.title}
          style={IS_PERSONAL_INVOICE_ENABLED ? {} : { marginBottom: 0 }}>
          Dane do faktury
        </div>
        <div className={styles.menu}>
          {IS_PERSONAL_INVOICE_ENABLED && (
            <InvoiceTypeTabs selected={activeTab} onTabChange={setActiveTab} />
          )}
          <ModalAddInvoiceAddress
            mode={FormMode.create}
            trigger={
              <Button fullWidth size='medium' variant='primary'>
                Dodaj nowy podmiot
              </Button>
            }
          />
        </div>
      </div>

      {activeTab === InvoiceType.company && companyPanel}
      {IS_PERSONAL_INVOICE_ENABLED &&
        activeTab === InvoiceType.personal &&
        personalPanel}
    </div>
  )
}

export default ProfileInvoiceDetails
